import { useEffect, useMemo, useRef, useState } from "react";
import {
  Search, MapPin, Loader2, Check, Globe2, Clock,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { toast } from "sonner";

const LS_RECENT_KEY = "tanakh_recent_locations";
// Single source of truth on the FE for "the user's chosen zmanim location".
// Replaces the legacy `tanakh_zmanim_loc` (catalog-id string only) key —
// the new value is the FULL descriptor JSON so custom (search/GPS)
// picks survive across sessions, not just catalog picks.
const LS_PREF_LOC   = "tanakh_pref_location";

// 14 catalog cities — same as the back-end's predefined list. Stable
// "popular" picks for fast access.
const POPULAR = [
  { id: "jerusalem",  name: "ירושלים",     country: "IL" },
  { id: "telaviv",    name: "תל אביב",     country: "IL" },
  { id: "haifa",      name: "חיפה",        country: "IL" },
  { id: "bneibrak",   name: "בני ברק",     country: "IL" },
  { id: "betshemesh", name: "בית שמש",     country: "IL" },
  { id: "modiin",     name: "מודיעין",     country: "IL" },
  { id: "netanya",    name: "נתניה",       country: "IL" },
  { id: "ashdod",     name: "אשדוד",       country: "IL" },
  { id: "beersheva",  name: "באר שבע",     country: "IL" },
  { id: "beitar",     name: "ביתר עילית",  country: "IL" },
  { id: "newyork",    name: "ניו יורק",    country: "US" },
  { id: "brooklyn",   name: "ברוקלין",     country: "US" },
  { id: "losangeles", name: "לוס אנג'לס",  country: "US" },
  { id: "miami",      name: "מיאמי",       country: "US" },
];


/**
 * Read the user's recent-locations from BE (when logged in) or from
 * localStorage. Always returns an array.
 */
async function loadRecents(isAuthed) {
  if (isAuthed) {
    try {
      const d = await api.getRecentLocations();
      return d?.items || [];
    } catch { /* fall through */ }
  }
  try {
    const raw = localStorage.getItem(LS_RECENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

/**
 * Push a location to the recents list. Mirrors the BE's dedupe+FIFO@5
 * semantics for anonymous users.
 */
async function persistRecent(entry, isAuthed) {
  if (isAuthed) {
    try { await api.addRecentLocation(entry); } catch {}
    return;
  }
  try {
    const raw = localStorage.getItem(LS_RECENT_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const key = entry.geonameid
      ? `gid:${entry.geonameid}`
      : (entry.latitude != null && entry.longitude != null
          ? `ll:${entry.latitude.toFixed(3)}:${entry.longitude.toFixed(3)}`
          : `name:${(entry.name_en || entry.name || "").toLowerCase()}`);
    const dKey = (e) => e.geonameid
      ? `gid:${e.geonameid}`
      : (e.latitude != null && e.longitude != null
          ? `ll:${(+e.latitude).toFixed(3)}:${(+e.longitude).toFixed(3)}`
          : `name:${(e.name_en || e.name || "").toLowerCase()}`);
    const filtered = list.filter((e) => dKey(e) !== key);
    const next = [{ ...entry, selected_at: new Date().toISOString() }, ...filtered].slice(0, 5);
    localStorage.setItem(LS_RECENT_KEY, JSON.stringify(next));
  } catch {}
}


/**
 * LocationPicker — global city search + GPS + recents + popular.
 *
 * `current` may be either a catalog id string (e.g. "jerusalem") or
 * a descriptor object with { geonameid? | latitude+longitude+tzid, name }.
 *
 * `onPick(descriptor)` is called with one of:
 *   - { kind: "catalog", id: "jerusalem", name, country }
 *   - { kind: "custom",  geonameid, name, name_en, display_name, country, cc, latitude?, longitude?, tzid? }
 *
 * The caller is responsible for translating that into BE zmanim query
 * params (catalog → `location=…`, custom → `geonameid=…` or lat/lon).
 */
export default function LocationPicker({
  open, onClose, current, onPick, isAuthed,
}) {
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [recents, setRecents] = useState([]);
  const [gpsLoading, setGpsLoading] = useState(false);
  const debounceRef = useRef(null);

  // Load recents whenever the picker opens.
  useEffect(() => {
    if (!open) return;
    setQ("");
    setResults([]);
    loadRecents(isAuthed).then(setRecents);
  }, [open, isAuthed]);

  // Debounced search on `q`.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const d = await api.searchLocations(term);
        setResults(d?.results || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, open]);

  const choose = async (descriptor, recentEntry) => {
    onPick(descriptor);
    // 1. localStorage — always (works for anonymous + as offline cache).
    try { localStorage.setItem(LS_PREF_LOC, JSON.stringify(descriptor)); } catch {}
    // 2. Server preference — for logged-in users so the choice
    //    survives across devices / browsers. Best-effort; a failure
    //    here doesn't break the UI.
    if (isAuthed) {
      try { await api.setMyLocation(descriptor); } catch {}
    }
    if (recentEntry) await persistRecent(recentEntry, isAuthed);
    onClose();
  };

  const pickCatalog = (c) => {
    choose(
      { kind: "catalog", id: c.id, name: c.name, country: c.country },
      // Catalog cities aren't added to recents — Popular row already
      // surfaces them. Keeps the recents list informative.
      null,
    );
  };

  const pickSearchResult = (r) => {
    const recent = {
      geonameid:    r.geonameid,
      name:         r.name,
      name_en:      r.name_en,
      display_name: r.display_name,
      country:      r.country,
      cc:           r.cc,
    };
    choose(
      {
        kind:      "custom",
        geonameid: r.geonameid,
        name:      r.name,
        name_en:   r.name_en,
        display_name: r.display_name,
        country:   r.country,
        cc:        r.cc,
      },
      recent,
    );
  };

  const pickRecent = (e) => {
    const descriptor = e.geonameid
      ? {
          kind: "custom",
          geonameid: e.geonameid,
          name: e.name, name_en: e.name_en,
          display_name: e.display_name, country: e.country, cc: e.cc,
        }
      : e.latitude != null && e.longitude != null && e.tzid
      ? {
          kind: "custom",
          latitude: e.latitude, longitude: e.longitude, tzid: e.tzid,
          name: e.name, name_en: e.name_en,
          display_name: e.display_name, country: e.country, cc: e.cc,
        }
      : null;
    if (descriptor) choose(descriptor, e);
  };

  const handleGps = async () => {
    if (!navigator.geolocation) {
      toast.error("הדפדפן לא תומך באיתור מיקום");
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const rev = await api.reverseGeocode(latitude, longitude);
          const recent = {
            name:         rev.name || rev.city || "המיקום שלי",
            name_en:      rev.name_en || rev.city,
            display_name: rev.display_name,
            country:      rev.country,
            cc:           rev.cc,
            latitude:     latitude,
            longitude:    longitude,
            tzid:         rev.tzid,
          };
          await choose(
            {
              kind:      "custom",
              latitude,  longitude,
              tzid:      rev.tzid,
              name:      recent.name,
              name_en:   recent.name_en,
              display_name: recent.display_name,
              country:   recent.country,
              cc:        recent.cc,
            },
            recent,
          );
          toast.success("המיקום זוהה", { description: recent.name });
        } catch (err) {
          toast.error("לא הצלחנו לזהות את המיקום", {
            description: err?.response?.data?.detail || "נסה שוב",
          });
        } finally {
          setGpsLoading(false);
        }
      },
      (err) => {
        setGpsLoading(false);
        if (err?.code === 1) {
          toast.error("הרשאת מיקום נדחתה", {
            description: "אפשר תמיד לחפש עיר באופן ידני.",
          });
        } else {
          toast.error("לא הצלחנו להשיג את המיקום שלך");
        }
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
    );
  };

  const isCurrentCatalog =
    typeof current === "string"
      ? (id) => id === current
      : (id) => current?.kind === "catalog" && current?.id === id;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-md max-h-[85vh] overflow-y-auto"
        dir="rtl"
        data-testid="location-picker"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-right">בחר מיקום</DialogTitle>
          <DialogDescription className="text-right text-xs">
            כל עיר בעולם — חפש או שתף את המיקום שלך.
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative mt-2">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="חפש עיר... (English / עברית)"
            dir="auto"
            className="pr-9"
            data-testid="location-search-input"
            autoFocus
          />
          {searching && (
            <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gold" />
          )}
        </div>

        {/* GPS */}
        <Button
          variant="outline"
          size="sm"
          className="w-full mt-3 justify-center gap-2 border-gold/40 hover:bg-gold/10"
          onClick={handleGps}
          disabled={gpsLoading}
          data-testid="location-gps-btn"
        >
          {gpsLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MapPin className="h-4 w-4 text-gold" strokeWidth={1.5} />
          )}
          השתמש במיקום שלי
        </Button>

        {/* Body — search results OR (recents + popular) */}
        <div className="mt-4 space-y-4">
          {q.trim().length >= 2 ? (
            <section data-testid="location-search-results">
              {searching && results.length === 0 ? (
                <div className="text-center text-xs text-muted-foreground py-6">
                  מחפש…
                </div>
              ) : results.length === 0 ? (
                <div className="text-center text-xs text-muted-foreground py-6">
                  לא נמצאו תוצאות — נסה שם אחר באנגלית.
                </div>
              ) : (
                <ul className="space-y-1">
                  {results.map((r) => (
                    <li key={r.geonameid}>
                      <button
                        onClick={() => pickSearchResult(r)}
                        data-testid={`search-result-${r.geonameid}`}
                        className="w-full text-right px-3 py-2.5 rounded-sm flex items-center justify-between gap-2 hover:bg-gold/10 border border-transparent hover:border-gold/30"
                      >
                        <span className="text-[11px] text-muted-foreground">
                          {r.flag || r.cc}
                        </span>
                        <span className="flex-1 min-w-0 text-right">
                          <div className="font-medium text-sm text-foreground truncate">{r.name}</div>
                          {r.hebrew && r.name !== r.name_en ? (
                            <div className="text-[10px] text-muted-foreground truncate">{r.display_name}</div>
                          ) : (
                            <div className="text-[10px] text-muted-foreground truncate">{r.display_name}</div>
                          )}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : (
            <>
              {recents.length > 0 && (
                <section data-testid="location-recents">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-gold/80 mb-2 justify-end">
                    <span>אחרונים שלך</span>
                    <Clock className="h-3 w-3" strokeWidth={1.5} />
                  </div>
                  <ul className="space-y-1">
                    {recents.map((e, i) => (
                      <li key={`${e.geonameid || ''}-${i}`}>
                        <button
                          onClick={() => pickRecent(e)}
                          data-testid={`recent-${e.geonameid || i}`}
                          className="w-full text-right px-3 py-2 rounded-sm flex items-center justify-between gap-2 hover:bg-gold/10 border border-transparent hover:border-gold/30"
                        >
                          <span className="text-[11px] text-muted-foreground">
                            {e.cc || ""}
                          </span>
                          <span className="flex-1 min-w-0 text-right">
                            <div className="font-medium text-sm text-foreground truncate">{e.name}</div>
                            {e.display_name && e.display_name !== e.name && (
                              <div className="text-[10px] text-muted-foreground truncate">{e.display_name}</div>
                            )}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section data-testid="location-popular">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-gold/80 mb-2 justify-end">
                  <span>מקומות פופולריים</span>
                  <Globe2 className="h-3 w-3" strokeWidth={1.5} />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                  {POPULAR.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => pickCatalog(c)}
                      data-testid={`popular-${c.id}`}
                      className={`text-xs px-3 py-2 rounded-sm border text-right transition ${
                        isCurrentCatalog(c.id)
                          ? "bg-gold/15 text-foreground border-gold/50"
                          : "border-border/40 text-foreground hover:border-gold/30 hover:bg-gold/5"
                      }`}
                    >
                      {isCurrentCatalog(c.id) && (
                        <Check className="h-3 w-3 text-gold inline-block ml-1" />
                      )}
                      {c.name}
                    </button>
                  ))}
                </div>
              </section>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
